<template>
  <div id="error-setting" class="s-base">
    <v-card class="s-panel" max-width="1000">
      <v-card-title class="s-title">障害設定画面</v-card-title>
      <v-container>
        <v-row>
          <v-col>
            <v-data-table
              :headers="tableHeaders"
              :items="errorSettingList"
              class="error-setting-list-table"
            >
              <template v-slot:item.delete="{ item }">
                <v-btn
                  color="error"
                  depressed
                  @click="confirmDelete(item.errorSettingId)"
                  ><v-icon color="white">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row no-gutters justify="space-between">
          <v-col cols="auto" class="px-0 py-5">
            <v-btn width="100" color="primary" @click="goToPreviousPage()"
              >戻る</v-btn
            >
          </v-col>
          <v-col cols="auto" class="px-0 py-5">
            <v-btn width="130" color="secondary" @click="dialog.input = true">
              検知文字追加</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <delete-dialog
      :confirmationDialogFlag.sync="dialog.delete"
      @deleteData="deleteErrorSetting()"
    />
    <message-dialog
      :messageDialogFlag.sync="dialog.message"
      :dialogText="messageDialogText"
    />
    <input-dialog
      :inputDialogFlag.sync="dialog.input"
      :errorSettingInputData.sync="errorSettingInput"
      :useTypeList="useTypeList"
      @registerErrorSetting="registerErrorSetting()"
    />
    <request-error-dialog
      :requestErrorDialogFlag.sync="dialog.requestError"
      :httpStatus="httpStatus"
      :message="requestErrorDialogMessage"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import errorSettingModule, {
  ErrorSettingData,
} from "../store/ErrorSettingModule";
import useTypeModule, { UseTypeList } from "../store/UseTypeModule";
import MessageDialog from "./MessageDialog.vue";
import ErrSttInputDialog from "./ErrSttInputDialog.vue";
import RequestErrorDialog from "./RequestErrorDialog.vue";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog.vue";
import { NETWORK_ERROR } from "../router";

@Component({
  components: {
    "delete-dialog": DeleteConfirmationDialog,
    "message-dialog": MessageDialog,
    "input-dialog": ErrSttInputDialog,
    "request-error-dialog": RequestErrorDialog,
  },
})
export default class ErrorSetting extends Vue {
  /** v-data-table用のヘッダープロパティ */
  tableHeaders = [
    { text: "検知文字", value: "detectKeyword", width: "50%" },
    { text: "対象サーバ", value: "useTypeName", width: "30%" },
    { text: "削除", value: "delete", align: "center", sortable: false },
  ];

  /** 各ダイアログのフラグ値 */
  dialog = {
    delete: false,
    input: false,
    success: false,
    message: false,
    requestError: false,
  };

  /** 画面遷移時のデータ読み込みエラー */
  mountedOK = false;

  /** httpステータスコード */
  httpStatus = 500;

  /** エラーダイアログのテキスト */
  requestErrorDialogMessage = "";

  /** メッセージダイアログのテキスト */
  messageDialogText = {
    title: "削除完了",
    message: "削除が完了しました。",
  };

  /** ダイアログのサンプルテキスト */
  dialogTemplate = {
    messageDialog: {
      succeedRegister: {
        title: "登録完了",
        message: "登録が完了しました。",
      },
      failRegisterForInputError: {
        title: "登録失敗",
        message: "登録に失敗しました。入力内容をご確認ください。",
      },
      succeedDelete: {
        title: "削除完了",
        message: "削除が完了しました。",
      },
    },

    requestErrorDialog: {
      failDelete: "削除に失敗しました。",
      failRegister: "登録に失敗しました。",
    },
  };

  /** 障害設定の入力情報 */
  errorSettingInput: {
    detectKeyword: string | null;
    useTypeCode: number | null;
  } = {
    detectKeyword: null,
    useTypeCode: null,
  };

  /** 削除する障害設定情報の障害設定ID */
  errorSettingIdOfDeleted = 0;

  /**
   * 障害設定情報リストをStoreから取得する。
   * @returns {ErrorSettingData[]} 障害設定情報リスト
   */
  get errorSettingList(): ErrorSettingData[] {
    if (this.httpStatus !== 200 && this.mountedOK !== true) {
      return [];
    }
    return errorSettingModule.errorSettingList;
  }

  /**
   * 用途種別リストをStoreから取得する。
   * @returns {UseTypeList} 用途種別リスト
   */
  get useTypeList(): UseTypeList {
    return useTypeModule.useTypeList;
  }

  /**
   * 障害設定情報をDBから削除する。
   */
  async deleteErrorSetting(): Promise<void> {
    let result = false;
    try {
      result = await errorSettingModule.deleteErrorSetting(
        this.errorSettingIdOfDeleted
      );
    } catch (error) {
      this.requestErrorDialogMessage =
        this.dialogTemplate.requestErrorDialog.failDelete;
      if (error instanceof Error) {
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else if (error.message === "404") {
          // 404 Not Found
          this.httpStatus = 404;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
    }
    if (result === true) {
      this.messageDialogText = this.dialogTemplate.messageDialog.succeedDelete;
      this.dialog.message = true;
      this.loadAllDataToStore();
    }
  }

  /**
   * 削除する障害設定情報のIDを保存し、削除確認ダイアログを表示する。
   * @param {number} errorSettingId 削除する障害設定情報のID
   */
  confirmDelete(errorSettingId: number): void {
    this.errorSettingIdOfDeleted = errorSettingId;
    this.dialog.delete = true;
  }

  /**
   * 障害設定情報をDBに登録する。
   */
  async registerErrorSetting(): Promise<void> {
    // 障害設定情報の値が全て入力されていない場合
    if (
      this.errorSettingInput.detectKeyword === null ||
      this.errorSettingInput.useTypeCode === null
    ) {
      this.messageDialogText =
        this.dialogTemplate.messageDialog.failRegisterForInputError;
      this.dialog.message = true;
      return;
    }

    let result = false;
    try {
      this.dialog.input = false;
      result = await errorSettingModule.createErrorSetting({
        detectKeyword: this.errorSettingInput.detectKeyword,
        useTypeCode: this.errorSettingInput.useTypeCode,
      });
    } catch (error) {
      this.requestErrorDialogMessage =
        this.dialogTemplate.requestErrorDialog.failRegister;
      if (error instanceof Error) {
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else if (error.message === "409") {
          // 409 Conflict Error
          this.httpStatus = 409;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
      return;
    }
    if (result === true) {
      this.messageDialogText =
        this.dialogTemplate.messageDialog.succeedRegister;
      this.dialog.message = true;
      this.loadAllDataToStore();
    }
  }

  /**
   * ひとつ前のページに遷移する。
   */
  goToPreviousPage(): void {
    this.$router.back();
  }

  /**
   * 各データをDBからStoreに取り込む
   */
  async loadAllDataToStore(): Promise<void> {
    const result = {
      errorSetting: false,
      useType: false,
    };
    try {
      result.errorSetting = await errorSettingModule.getErrorSettingList();
      result.useType = await useTypeModule.getUseTypeList();
    } catch (error) {
      this.requestErrorDialogMessage = "";
      if (error instanceof Error) {
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
    }
    if (result.errorSetting === true && result.useType === true) {
      this.httpStatus = 200;
    }
  }

  /**
   * 障害設定画面に遷移した時に実行
   */
  async mounted() {
    await this.loadAllDataToStore();
    if (this.httpStatus === 200) {
      this.mountedOK = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/scss/common.scss";
.error-setting-list-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  ::v-deep th {
    padding-top: 5px;
    padding-bottom: 5px;
    padding-left: 5px;
    border: none;
    background-color: #2b72de;
    word-break: break-all;
    span {
      font-size: 17px;
      font-weight: 400;
      color: #ffffff;
    }
    .v-icon {
      font-size: 20px !important;
      color: #d4d4d4 !important;
    }
  }
  ::v-deep td {
    word-break: break-all;
  }
  ::v-deep tr {
    td {
      font-size: 20px;
    }
  }
  ::v-deep tr:nth-child(even) {
    background-color: #f0ffff;
  }
}
</style>
